import {Fragment, useEffect, useState} from "react";
import {format} from "date-fns";
import {BreadCrumb, Table, CenteredModal, FormInput} from "~/components";
import {SkeletonTableLoading} from '~/components';
import ServicePlanAPI from "~/api/servicePlanApi";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";

const breadCrumbItem = [
    { text: "AI Chatbot Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Service Manager", to: "", isActive: true },
]
function ServicePlanManager() {
    const [reloadTable, setReloadTable] = useState(false);
    const [servicePlan, setServicePlan] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const tableConfig = {
        columns: [
            {
                name: 'ID',
                width: '40px',
                className: 'fw-bold',
                key: 'ServicePlanId'
            },
            {
                name: 'SERVICE TYPE',
                width: '150px',
                key: 'ServiceType'
            },
            {
                name: 'DESCRIPTION',
                width: '250px',
                key: 'Description'
            },
            {
                name: 'SERVICE NAME',
                width: '150px',
                key: 'Name'
            },
            {
                name: 'DISCOUNT',
                width: '100px',
                key: 'Discount',
                template : (row) => {
                    return (
                        <span>{row.Discount}%</span>
                    )
                }
            },
            {
                name: 'PRICE PER MONTH',
                width: '160px',
                key: "PricePerMonth",
                template : (row) => {
                    return (
                        <span>${row.PricePerMonth}</span>
                    )
                }
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                headerClassName: 'text-center',
                className: 'text-center',
                template: (row) => {
                    return (
                        <span><FontAwesomeIcon onClick={()=> handleEditServiceManager(row.ServicePlanId)} icon={faEdit} className="text-primary cursor-pointer"/></span>
                    )
                }
            }
        ],
        tableKey: "TableServiceManager"
    }

    useEffect(() => {
        setLoading(true);
        ServicePlanAPI.getAll()
            .then((res) => {
                setServicePlan(res.data);
            })
            .catch((err) => {
                ;
            })
            .finally(() => {
                setLoading(false);
            })
    }, [reloadTable]);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        getValues,
        watch,
        formState: { errors }
    } = useForm();

    const watchedDescriptions = watch("PlanDescriptions") || [];


    const handleEditServiceManager = (servicePlanId) => {
        ServicePlanAPI.getById(servicePlanId)
            .then((res) => {
                reset(res.data);
                setShowModal(true);
            })
            .catch((err) => {
                ;
            })
    }

    const onSubmitFrom = (data) => {
        ServicePlanAPI.update(data)
            .then((res) => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your service plan has been updated.</span>)
                    : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your service plan.</span>)
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while updating your service plan.</span>)
            })
            .finally(() => {
                setShowModal(false);
                setReloadTable(prev => !prev);
            })
    }

    function PlanDescriptionRow({ item, index, register, errors, watch, setValue }) {
        const isApplyChecked = watch(`PlanDescriptions[${index}].IsApply`);

        useEffect(() => {
            if (!isApplyChecked) {
                setValue(`PlanDescriptions[${index}].Amount`, "0");
            }
        }, [isApplyChecked, index, setValue]);

        return (
            <div className="row align-items-center">
                <div className="col-sm-6 col-12">
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            {...register(`PlanDescriptions[${index}].IsApply`)}
                            id={`planDescriptions-${index}`}
                        />
                        <label className="form-check-label" htmlFor={`planDescriptions-${index}`}>
                            {item.Description}
                        </label>
                    </div>
                </div>

                {item.IsNumber && (
                    <div className="position-relative col-sm-6 col-12">
                        <FormInput
                            showHeaderLabel={true}
                            name={`PlanDescriptions[${index}].Amount`}
                            errors={errors}
                            required={isApplyChecked}
                            disabled={!isApplyChecked}
                            maxLengthText=""
                            label="Assign Count"
                            validation={{
                                ...register(`PlanDescriptions[${index}].Amount`, {
                                    required: isApplyChecked ? "Count is required." : false,
                                    ...(isApplyChecked && {
                                        pattern: {
                                            value: /^[0-9]+$/,
                                            message: "Only numbers are allowed.",
                                        },
                                        validate: (value) => {
                                            const num = parseInt(value, 10);
                                            if (isNaN(num)) return "Invalid number.";
                                            if (num < 0) return "Must be at least 0.";
                                            if (num > 100) return "Must be no more than 100.";
                                            return true;
                                        },
                                    }),
                                }),
                            }}
                        />
                        <span className="position-absolute fs-9 input-unit fw-bold">{item.Unit}</span>
                    </div>
                )}
            </div>
        );
    }

    return(
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-5">Service Manager</h2>
            {loading ? (
                <SkeletonTableLoading columns={tableConfig.columns} rows={10} />
            ) : (
                <Table tableConfig={{...tableConfig, data : servicePlan}}/>
            )}
            <CenteredModal
                show={showModal}
                onClose={() => setShowModal(false)}
                title="Edit Service Manager"
                formId="formEditServiceManager"
                modalHeight="710px"
            >
                <form onSubmit={handleSubmit(onSubmitFrom)} id="formEditServiceManager">
                    <div>
                        <div className="mb-3 nav nav-underline" role="tablist">
                            <div className="nav-item">
                                <a
                                    className="nav-link active"
                                    id="tab-main-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-main"
                                    role="tab"
                                    aria-controls="tab-main"
                                    aria-selected="true"
                                >
                                    Main
                                </a>
                            </div>
                            <div className="nav-item">
                                <a
                                    className="nav-link"
                                    id="tab-mapping-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-mapping"
                                    role="tab"
                                    aria-controls="tab-mapping"
                                    aria-selected="false"
                                >
                                    Description Mapping
                                </a>
                            </div>
                        </div>

                        <div className="tab-content">
                            <div
                                className="tab-pane fade show active"
                                id="tab-main"
                                role="tabpanel"
                                aria-labelledby="tab-main-tab"
                            >
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name="ServiceType"
                                               errors={errors}
                                               required={true}
                                               disabled={true}
                                               maxLengthText=""
                                               label="Assign Service Type"
                                               validation={{
                                                   ...register("ServiceType", {
                                                       required: "Service type is required."
                                                   })
                                               }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name="Name"
                                               errors={errors}
                                               required={true}
                                               label="Assign Service Name"
                                               maxLengthText="max length 255 character"
                                               validation={{
                                                   ...register("Name", {
                                                       required: "Service name is required."
                                                   })
                                               }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name="Description"
                                               errors={errors}
                                               textarea={true}
                                               required={true}
                                               label="Assign Description"
                                               validation={{
                                                   ...register("Description", {
                                                       required: "Description is required."
                                                   })
                                               }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <div className="row">
                                        <div className="col-sm-6 col-12 position-relative">
                                            <FormInput showHeaderLabel={true}
                                                       name = "Discount"
                                                       errors={errors}
                                                       required= {true}
                                                       maxLengthText=""
                                                       label="Assign Discount"
                                                       validation={{...register('Discount', {
                                                               required: 'Discount is required.',
                                                               pattern: {
                                                                   value: /^[0-9]+$/,
                                                                   message: 'Only numbers are allowed.'
                                                               },
                                                               validate: value => {
                                                                   const num = parseInt(value, 10);
                                                                   if (isNaN(num)) return 'Invalid number.';
                                                                   if (num < 0) return 'Must be at least 0.';
                                                                   if (num > 100) return 'Must be no more than 100.';
                                                                   return true;
                                                               }
                                                           })}
                                                       }
                                            />
                                            <span className="position-absolute input-unit fw-bold">%</span>
                                        </div>
                                        <div className="col-sm-6 col-12 position-relative">
                                            <FormInput showHeaderLabel={true}
                                                       name = "PricePerMonth"
                                                       errors={errors}
                                                       required= {true}
                                                       maxLengthText=""
                                                       label="Assign PricePerMonth"
                                                       validation={{...register("PricePerMonth", {
                                                               required: "Price Per Month is required."
                                                           })}}
                                            />
                                            <span className="position-absolute input-unit fw-bold">$</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="tab-pane fade"
                                id="tab-mapping"
                                role="tabpanel"
                                aria-labelledby="tab-mapping-tab"
                            >
                                {getValues().PlanDescriptions?.map((item, index) => (
                                    <PlanDescriptionRow
                                        key={index}
                                        item={item}
                                        index={index}
                                        register={register}
                                        errors={errors}
                                        watch={watch}
                                        setValue={setValue}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </form>
            </CenteredModal>
        </Fragment>
    )
}
export default ServicePlanManager;